/**
 * Permissions API module
 */
import { coreClient, apiRequest } from '../client.js';
import type {
  BotName,
  Permission,
  PermissionCheck,
  PermissionCheckResult,
  ChannelPermissionCheck,
  ChannelPermissionResult,
  GrantPermissionRequest,
  RevokePermissionRequest,
} from '../types.js';

export const permissionsApi = {
  check: (params: PermissionCheck) =>
    apiRequest<PermissionCheckResult>(coreClient, {
      method: 'POST',
      url: '/permissions/check',
      data: params,
    }),

  checkChannel: (params: ChannelPermissionCheck) =>
    apiRequest<ChannelPermissionResult>(coreClient, {
      method: 'POST',
      url: '/permissions/channel-check',
      data: params,
    }),

  getUserLevel: (userId: string, guildId: string) =>
    apiRequest<{
      userId: string;
      guildId: string;
      level: 'god_mode' | 'admin' | 'target' | 'user';
      hasPermission: boolean;
    }>(coreClient, {
      method: 'GET',
      url: `/permissions/user/${userId}/level`,
      params: { guildId },
    }),

  batchCheck: (checks: PermissionCheck[]) =>
    apiRequest<{ results: PermissionCheckResult[] }>(coreClient, {
      method: 'POST',
      url: '/permissions/batch-check',
      data: { checks },
    }),

  getForGuild: (guildId: string, botName?: BotName) =>
    apiRequest<{
      guildId: string;
      botName?: string;
      permissions: Permission[];
      total: number;
    }>(coreClient, {
      method: 'GET',
      url: `/permissions/guild/${guildId}`,
      params: botName ? { botName } : undefined,
    }),

  grant: (request: GrantPermissionRequest) =>
    apiRequest<{
      success: boolean;
      permissionId: string;
      message: string;
    }>(coreClient, {
      method: 'POST',
      url: '/permissions/grant',
      data: request,
    }),

  revoke: (request: RevokePermissionRequest) =>
    apiRequest<{
      success: boolean;
      message: string;
    }>(coreClient, {
      method: 'POST',
      url: '/permissions/revoke',
      data: request,
    }),

  update: (permission: Partial<Permission>) =>
    apiRequest<Permission>(coreClient, {
      method: 'PUT',
      url: '/permissions',
      data: permission,
    }),
};

export default permissionsApi;
